
import { AuthMode, authenticate } from "../api/auth.js";
import { AppTextInput } from "./app-text-input.js";
import { AppButton } from "./app-button.js";

const styles = css`
  :host {
    display: block;
    width: 100%;
  }

  .card {
    background: var(--card, #ffffff);
    border: 1px solid var(--border, #e4e4e7);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
    box-sizing: border-box;
    padding: 28px 24px;
  }

  .header {
    display: grid;
    gap: 6px;
    margin-bottom: 22px;
  }

  .header h1 {
    color: var(--card-foreground, var(--foreground));
    font-size: 22px;
    font-weight: 600;
    line-height: 1.2;
    margin: 0;
  }

  .header p {
    color: var(--muted-foreground);
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
  }

  form {
    display: grid;
    gap: 16px;
  }

  app-button.submit {
    display: block;
    margin-top: 4px;
    width: 100%;
  }

  .switch {
    color: var(--muted-foreground);
    font-size: 14px;
    margin: 8px 0 0;
    text-align: center;
  }

  .switch a {
    color: var(--foreground);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  .error {
    background: var(--destructive-muted, #fff1f0);
    border-radius: 8px;
    color: var(--destructive, #9f1d16);
    font-size: 14px;
    margin: 0;
    padding: 10px 12px;
  }
`;

async function submit(host: HTMLElement & { mode?: string }, root: ShadowRoot): Promise<void> {
  const form = host as HTMLElement & { error: string; loading: boolean };
  if (form.loading) return;

  const username = root.querySelector<HTMLElement & { value: string }>('app-text-input[data-name="username"]')!.value;
  const password = root.querySelector<HTMLElement & { value: string }>('app-text-input[data-name="password"]')!.value;

  form.loading = true;
  form.error = "";
  try {
    await authenticate((host.mode ?? "login") as AuthMode, username, password);
    host.dispatchEvent(new CustomEvent("auth-success", { bubbles: true, composed: true }));
  } catch (error) {
    form.error = error instanceof Error ? error.message : "Auth request failed.";
  } finally {
    form.loading = false;
  }
}

export class AuthForm extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AuthForm)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["mode","data-mode","error","data-error","loading","data-loading"];

  #mode = "login";
  #error = "";
  #loading = false;
  #modeSet = false;
  #errorSet = false;
  #loadingSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #textStart4;
  #textEnd4;
  #textLast4;
  #textStart5;
  #textEnd5;
  #textLast5;
  #textStart6;
  #textEnd6;
  #textLast6;
  #textStart7;
  #textEnd7;
  #textLast7;
  #attr0;
  #attr1;
  #attr2;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #attrLast5;
  #submitButton = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="submitButton"]') : null; } }; })();
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get mode() {
    return this.#mode;
  }

  set mode(value) {
    const raw = value ?? "login";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#mode, next)) return;
    this.#mode = next;
    this.#modeSet = true;
    this.#updateBindings('mode');
  }

  get error() {
    return this.#error;
  }

  set error(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#error, next)) return;
    this.#error = next;
    this.#errorSet = true;
    this.#updateBindings('error');
  }

  get loading() {
    return this.#loading;
  }

  set loading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#loading, next)) return;
    this.#loading = next;
    this.#loadingSet = true;
    this.#updateBindings('loading');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-mode')) this.mode = this.dataset.mode;
    if (this.hasAttribute('data-error')) this.error = this.dataset.error;
    if (this.hasAttribute('data-loading')) this.loading = this.dataset.loading;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.mode = props.mode;
      this.error = props.error;
      this.loading = props.loading;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#modeSet) this.mode = this.dataset.mode;
    if (!this.#errorSet) this.error = this.dataset.error;
    if (!this.#loadingSet) this.loading = this.dataset.loading;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'mode':
      case 'data-mode':
        this.mode = newValue;
        break;
      case 'error':
      case 'data-error':
        this.error = newValue;
        break;
      case 'loading':
      case 'data-loading':
        this.loading = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const isLogin = this.#mode === "login";
    const title = isLogin ? "Log in" : "Create account";
    const description = isLogin ? "Enter your credentials to access S.T.R.I.D.E." : "Create an account to get started.";
    const submitLabel = isLogin ? "Log in" : "Register";
    const switchPrompt = isLogin ? "Don't have an account? " : "Already have an account? ";
    const switchLabel = isLogin ? "Register" : "Log in";
    const switchHref = isLogin ? "/auth/register" : "/auth/login";
    return `<style>${this.#escText(styles)}</style><div class="card"><div class="header"><h1><!--argon-text:0-->${this.#escText(title)}<!--/argon-text:0--></h1><p><!--argon-text:1-->${this.#escText(description)}<!--/argon-text:1--></p></div><form data-argon="3209"><!--argon-text:2-->${(this.#error !== "") ? `<p class="error">${this.#escText(this.#error)}</p>` : ""}<!--/argon-text:2--><app-text-input data-label="Username" data-name="username" data-autocomplete="username" data-disabled="${this.#attrEsc(this.#loading)}" data-required="${this.#attrEsc(true)}" data-argon="3593" data-argon-bind="0"><!--argon-text:3-->${""}<!--/argon-text:3--></app-text-input><app-text-input data-label="Password" data-name="password" data-kind="password" data-autocomplete="${this.#attrEsc(isLogin ? "current-password" : "new-password")}" data-disabled="${this.#attrEsc(this.#loading)}" data-required="${this.#attrEsc(true)}" data-argon="3913" data-argon-bind="1"><!--argon-text:4-->${""}<!--/argon-text:4--></app-text-input><app-button class="submit" data-argon-ref="submitButton" data-argon="4069"><!--argon-text:5-->${""}${this.#escText(submitLabel)}<!--/argon-text:5--></app-button><p class="switch"><!--argon-text:6-->${this.#escText(switchPrompt)}<!--/argon-text:6--><a href="${this.#escAttr(switchHref)}" data-argon-bind="2"><!--argon-text:7-->${this.#escText(switchLabel)}<!--/argon-text:7--></a></p></form></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
        case 'argon-text:4':
          this.#textStart4 = marker;
          break;
        case '/argon-text:4':
          this.#textEnd4 = marker;
          break;
        case 'argon-text:5':
          this.#textStart5 = marker;
          break;
        case '/argon-text:5':
          this.#textEnd5 = marker;
          break;
        case 'argon-text:6':
          this.#textStart6 = marker;
          break;
        case '/argon-text:6':
          this.#textEnd6 = marker;
          break;
        case 'argon-text:7':
          this.#textStart7 = marker;
          break;
        case '/argon-text:7':
          this.#textEnd7 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    this.#submitButton.current?.toggleAttribute("loading", this.#loading);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const isLogin = (prop === undefined || prop === 'mode' || prop === 'loading') ? (this.#mode === "login") : undefined;
    const title = (prop === undefined || prop === 'mode') ? (isLogin ? "Log in" : "Create account") : undefined;
    const description = (prop === undefined || prop === 'mode') ? (isLogin ? "Enter your credentials to access S.T.R.I.D.E." : "Create an account to get started.") : undefined;
    const submitLabel = (prop === undefined || prop === 'mode') ? (isLogin ? "Log in" : "Register") : undefined;
    const switchPrompt = (prop === undefined || prop === 'mode') ? (isLogin ? "Don't have an account? " : "Already have an account? ") : undefined;
    const switchLabel = (prop === undefined || prop === 'mode') ? (isLogin ? "Register" : "Log in") : undefined;
    const switchHref = (prop === undefined || prop === 'mode') ? (isLogin ? "/auth/register" : "/auth/login") : undefined;
    if (prop === undefined || prop === 'mode') {
      const __argonNext = `${this.#escText(title)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'mode') {
      const __argonNext = `${this.#escText(description)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'error') {
      const __argonNext = `${(this.#error !== "") ? `<p class="error">${this.#escText(this.#error)}</p>` : ""}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'loading') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if (prop === undefined || prop === 'mode' || prop === 'loading') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast4) {
        this.#textLast4 = __argonNext;
        this.#replaceRange(this.#textStart4, this.#textEnd4, __argonNext);
      }
    }
    if (prop === undefined || prop === 'mode') {
      const __argonNext = `${""}${this.#escText(submitLabel)}`;
      if (__argonNext !== this.#textLast5) {
        this.#textLast5 = __argonNext;
        this.#replaceRange(this.#textStart5, this.#textEnd5, __argonNext);
      }
    }
    if (prop === undefined || prop === 'mode') {
      const __argonNext = `${this.#escText(switchPrompt)}`;
      if (__argonNext !== this.#textLast6) {
        this.#textLast6 = __argonNext;
        this.#replaceRange(this.#textStart6, this.#textEnd6, __argonNext);
      }
    }
    if (prop === undefined || prop === 'mode') {
      const __argonNext = `${this.#escText(switchLabel)}`;
      if (__argonNext !== this.#textLast7) {
        this.#textLast7 = __argonNext;
        this.#replaceRange(this.#textStart7, this.#textEnd7, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'loading') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#loading)}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-disabled', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr0) {
      const __argonNext = `${this.#attrRaw(true)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-required', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'mode') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(isLogin ? "current-password" : "new-password")}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr1.setAttribute('data-autocomplete', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'loading') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(this.#loading)}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr1.setAttribute('data-disabled', __argonNext);
      }
    }
    if ((prop === undefined) && this.#attr1) {
      const __argonNext = `${this.#attrRaw(true)}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr1.setAttribute('data-required', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'mode') && this.#attr2) {
      const __argonNext = `${switchHref}`;
      if (__argonNext !== this.#attrLast5) {
        this.#attrLast5 = __argonNext;
        this.#attr2.setAttribute('href', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'loading')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '3209').forEach(el => this.#listen(el, '3209', 'submit', (event: Event) => {
    event.preventDefault();
    void submit(this, this.shadowRoot!);
}));
    this.#eventTargets(scope, '3593').forEach(el => this.#listen(el, '3593', 'commit', () => void submit(this, this.shadowRoot!)));
    this.#eventTargets(scope, '3913').forEach(el => this.#listen(el, '3913', 'commit', () => void submit(this, this.shadowRoot!)));
    this.#eventTargets(scope, '4069').forEach(el => this.#listen(el, '4069', 'click', (event: Event) => {
    event.preventDefault();
    void submit(this, this.shadowRoot!);
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
